/**
 * Step records, server-paged, with edit and delete.
 *
 * A step record is one source's contribution — a trail recording, a walk, an auto-trail or an
 * adventure run — and lifetime steps and every step achievement are the sum of them. So the header
 * shows the lifetime total the server computed, not a sum of the page on screen.
 */

import { useState } from 'react'
import { Chip, Typography } from '@mui/material'
import { Delete, DirectionsWalk, Edit } from '@mui/icons-material'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { DataTable, type DataTableColumn } from '@components/common/DataTable'
import { userDataApi } from '@api/endpoints/userData'
import type { AdminUserStepEventDto } from '@api/types/userData'
import { EditStepEventDialog } from './UserDataEditDialogs'
import { UserDataActionDialog } from './UserDataActionDialog'
import { UserDataChangeResultDialog } from './UserDataChangeResultDialog'
import { UserDataSection } from './UserDataSection'
import { adminUserStepEventsKey } from './userQueryKeys'
import { errorMessage, formatDate } from './userFormatting'
import { useUserDataChange } from './useUserDataChange'
import { useIsAdmin } from './useIsAdmin'

const SOURCE_LABELS: Record<string, string> = {
  trailRecording: 'Trail recording',
  trailWalk: 'Trail walk',
  autoTrail: 'Auto trail',
  adventure: 'Adventure',
}

type StepAction = { kind: 'edit' | 'delete'; row: AdminUserStepEventDto }

export function UserStepsSection({ userId }: { userId: string }) {
  const change = useUserDataChange(userId)
  // Moderators see this read-only; every write here is Admin-only on the API too.
  const isAdmin = useIsAdmin()
  const [action, setAction] = useState<StepAction | null>(null)
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(25)

  const { data, isLoading, error } = useQuery({
    queryKey: [...adminUserStepEventsKey(userId), page, pageSize],
    // The endpoint pages from 1; DataTable from 0.
    queryFn: () => userDataApi.getStepEvents(userId, page + 1, pageSize),
    placeholderData: keepPreviousData,
  })

  // Deleting the last record on the last page would otherwise leave the table on an empty page.
  const lastPage = data ? Math.max(0, Math.ceil(data.totalCount / pageSize) - 1) : 0
  if (data && page > lastPage) setPage(lastPage)

  const close = () => {
    setAction(null)
    change.resetError()
  }

  const columns: DataTableColumn<AdminUserStepEventDto>[] = [
    { key: 'occurred', header: 'When', primary: true, render: (e) => formatDate(e.occurredAt) },
    {
      key: 'source',
      header: 'Source',
      render: (e) => <Chip size="small" variant="outlined" label={SOURCE_LABELS[e.source] ?? e.source} />,
    },
    { key: 'title', header: 'Title', hideBelow: 'md', render: (e) => e.title || '—' },
    { key: 'steps', header: 'Steps', align: 'right', render: (e) => e.steps.toLocaleString() },
  ]

  return (
    <UserDataSection
      title="Steps"
      icon={<DirectionsWalk fontSize="small" />}
      aside={
        data && (
          <Typography variant="body2" color="text.secondary">
            Lifetime total: <strong>{data.totalSteps.toLocaleString()}</strong> steps
          </Typography>
        )
      }
    >
      <DataTable
        rows={data?.items ?? []}
        columns={columns}
        getRowKey={(e) => e.id}
        loading={isLoading}
        error={error ? errorMessage(error, 'Failed to load step records') : null}
        emptyMessage="No step records"
        dense
        page={page}
        pageSize={pageSize}
        total={data?.totalCount ?? 0}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        getRowActions={(e) =>
          isAdmin
            ? [
                { key: 'edit', label: 'Edit steps', icon: <Edit fontSize="small" />, onClick: () => setAction({ kind: 'edit', row: e }) },
                {
                  key: 'delete',
                  label: 'Delete step record',
                  icon: <Delete fontSize="small" />,
                  color: 'error',
                  onClick: () => setAction({ kind: 'delete', row: e }),
                },
              ]
            : []
        }
      />

      {action?.kind === 'edit' && (
        <EditStepEventDialog
          key={action.row.id}
          row={action.row}
          pending={change.pending}
          error={change.error}
          onSubmit={(request) => change.submit(() => userDataApi.editStepEvent(userId, action.row.id, request), close)}
          onClose={close}
        />
      )}

      {action?.kind === 'delete' && (
        <UserDataActionDialog
          title="Delete step record?"
          message={`${action.row.steps.toLocaleString()} steps from ${action.row.title || SOURCE_LABELS[action.row.source] || action.row.source}, ${formatDate(action.row.occurredAt)}. Lifetime steps and step achievements are recalculated. This cannot be undone.`}
          confirmLabel="Delete"
          destructive
          pending={change.pending}
          error={change.error}
          onSubmit={(reason) => change.submit(() => userDataApi.deleteStepEvent(userId, action.row.id, reason), close)}
          onClose={close}
        />
      )}

      <UserDataChangeResultDialog open={change.resultOpen} result={change.result} onClose={change.closeResult} />
    </UserDataSection>
  )
}
